import clsx from 'clsx';
import { useMemo } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import IndeterminateProgress from '../../components/Common/IndeterminateProgress';
import ActivateTotpOverlay from '../../components/Common/ActivateTotpOverlay';
import AppUpdatedOverlay from '../../components/Common/AppUpdatedOverlay';
import SessionExpiredOverlay from '../../components/Common/SessionExpiredOverlay';
import TooManyRequestsOverlay from '../../components/Common/TooManyRequestsOverlay';
import TransferProgressDrawers from '../../components/Common/TransferProgressDrawers';
import ErrorBoundary from '../../components/Core/ErrorBoundary';
import Header from '../../components/Core/Header';
import RightPanel from '../../components/Core/RightPanel';
import { useFilePasswordAccess } from '../../hooks/useFilePasswordAccess';
import { useRightPanel } from '../../hooks/useRightPanel';
import { useAppStore } from '../../store/store';
import { isViewerEmbedSession } from '../../utils/viewerWebviewAuth';

interface ExternalLayoutProps {
  isHeaderVisible?: boolean;
}

/**
 * ExternalLayout is the main layout component for external/public pages.
 *
 * - Optionally shows the Header unless suppressed by the "acToken" URL parameter (anti-capture mode)
 *   or by sessionStorage `viewerEmbed` (webview token QS, after strip).
 * - Wraps routed content (via <Outlet />) in an error boundary and scrollbar.
 * - Supplies RightPanel, transfer progress UI, and session expiration overlay.
 * - Responsive paddings/margins to accommodate header and side panels.
 */
function ExternalLayout({ isHeaderVisible = true }: ExternalLayoutProps) {
  // Right panel controls from custom hook (Zustand or Context)
  const { isRightPanelOpen, closeRightPanel, hasRightPanelCloseButton, rightPanelContent } =
    useRightPanel();
  const isGlobalLoaderVisible = useAppStore((s) => s.isGlobalLoaderVisible);

  // Get current query params from the URL (to detect "acToken")
  const [searchParams] = useSearchParams();

  /**
   * Determines whether the header should be visible.
   * - Hide for anti-capture links (when acToken param is present)
   * - Hide for webview viewer embed (sessionStorage viewerEmbed)
   * - Hide if explicitly set by props
   */
  const shouldShowHeader = useMemo(() => {
    const hasAcToken = searchParams.get('acToken') !== null;
    const isViewerEmbed = isViewerEmbedSession();
    return isHeaderVisible && !hasAcToken && !isViewerEmbed;
  }, [isHeaderVisible, searchParams]);

  return (
    <>
      <div className="relative flex h-full w-full grow overflow-hidden bg-white">
        <AppUpdatedOverlay />
        {/* Overlay shown when user's session expires */}
        <SessionExpiredOverlay />
        {/* Overlay shown when backend returns 429 Too Many Requests; survives reloads. */}
        <TooManyRequestsOverlay />
        <ActivateTotpOverlay />
        {/* Handler for folder password access */}
        {useFilePasswordAccess()}

        {/* Conditionally render application header */}
        {shouldShowHeader && <Header />}

        {/* Global loading bar: below header when visible, otherwise at top */}
        <IndeterminateProgress
          className={clsx(
            'absolute left-0 z-999 w-full p-0 transition-all duration-300',
            shouldShowHeader ? 'top-[var(--app-header-stack-height,3.5rem)]' : 'top-0',
            isGlobalLoaderVisible ? 'opacity-100' : 'opacity-0',
          )}
        />

        {/* Main content area, adjust margin/padding for header and right panel */}
        <main
          className={clsx(
            'w-screen flex-1 shrink overflow-x-hidden transition-all duration-300',
            shouldShowHeader ? 'mt-[var(--app-header-stack-height,3.5rem)]' : 'mt-0',
            // Match RightPanel max-w-[480px] (was 350 when panel was 350).
            isRightPanelOpen ? 'xl:mr-[480px]' : 'mr-0',
          )}
        >
          <div className="h-full overflow-auto">
            {/* Catch and display errors in child routes/components */}
            <ErrorBoundary>
              <OverlayScrollbarsComponent
                options={{
                  scrollbars: {
                    autoHide: 'leave',
                  },
                }}
                defer
                style={{ height: '100%', overflow: 'auto' }}
              >
                {/* Render nested route/component */}
                <Outlet />
              </OverlayScrollbarsComponent>
            </ErrorBoundary>
          </div>
        </main>

        {/* Sliding details or actions panel (only shown if open) */}
        <RightPanel
          isHeaderVisible={shouldShowHeader}
          isOpen={isRightPanelOpen}
          onClose={closeRightPanel}
          hasCloseButton={hasRightPanelCloseButton}
        >
          {rightPanelContent}
        </RightPanel>

        {/* UI for showing active file upload/download progress */}
        <TransferProgressDrawers />
      </div>
    </>
  );
}

export default ExternalLayout;
